import { Tab, Tabs, Box, Button } from "@mui/material";

import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { useState } from "react";
import { bash, python, java, cSharp, nodejs, php, ruby, vb } from "./templates";
import atomDark from "react-syntax-highlighter/dist/cjs/styles/prism/atom-dark";
import oneLight from "react-syntax-highlighter/dist/cjs/styles/prism/one-light";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { copyToClipboardWrapper } from "@/utils/clipBoardUtils";

const languagesOnboarding = [
  { id: "bash", name: "Curl", code: bash },
  { id: "python", name: "Python", code: python },
  { id: "javascript", name: "NodeJS", code: nodejs },
];

const languagesProxy = [
  ...languagesOnboarding,
  { id: "java", name: "Java", code: java },
  { id: "csharp", name: "C#", code: cSharp },
  { id: "php", name: "Php", code: php },
  { id: "ruby", name: "Ruby", code: ruby },
  { id: "vbnet", name: "VB", code: vb },
];

interface IProps {
  protocol: string;
  proxyUserName: string;
  password: string;
  domainName: string;
  authenticationMethod: string;
  theme?: "light" | "dark";
  port?: string;
  onboarding?: boolean;
}

export default function CodeExample({
  protocol,
  proxyUserName,
  password,
  domainName,
  authenticationMethod,
  theme = "light",
  port,
  onboarding,
}: IProps) {
  const [value, setValue] = useState(0);
  const [copyStatus, setCopyStatus] = useState<string>("");

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleCopy = async (text: string) => {
    try {
      await copyToClipboardWrapper(text);
      setCopyStatus("Copied");
      setTimeout(() => setCopyStatus(""), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const args = {
    protocol,
    proxyUserName,
    password,
    domainName,
    authenticationMethod,
    port,
  };

  const languages = onboarding ? languagesOnboarding : languagesProxy;

  const activeLanguage = languages[value];
  const activeCode = activeLanguage.code(args);

  return (
    <>
      <Box sx={{ maxWidth: "100%", bgcolor: "background.paper" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-flexContainer": { justifyContent: "space-around" },
          }}
        >
          {languages.map((language) => (
            <Tab key={language.id} label={language.name} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
        <Box
          sx={{
            "& pre": {
              "& code": {
                whiteSpace: "pre-wrap !important",
              },
            },
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              position: "relative",
            }}
          >
            <SyntaxHighlighter
              language={activeLanguage.id}
              style={theme === "dark" ? atomDark : oneLight}
              customStyle={{ width: "100%" }}
            >
              {activeCode}
            </SyntaxHighlighter>
            <Button
              sx={{
                bgcolor: "grey.300",
                color: "text.primary",
                position: "absolute",
                right: 8,
                bottom: 16,
              }}
              startIcon={<ContentCopyIcon />}
              onClick={() => handleCopy(activeCode)}
            >
              {copyStatus || "Copy"}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  );
}
